import { CompetencyMapQuestionsQuery } from "@generated/graphql";
import { Card, LinearProgress, Stack, Typography } from "@mui/material";
import { secondsToMinutes } from "helpers/index";

type SkillProgressInfoData = { id: string; title: string; confirmedQuestionsCount: number };

type ProgressInfoData = [{ confirmedQuestionsCount: number; progressPercent: number }, SkillProgressInfoData[]];

interface ProgressInfoProps {
  competencyMap: CompetencyMapQuestionsQuery["competencyMap"];
}

const ProgressInfo = ({ competencyMap }: ProgressInfoProps) => {
  const getProgressInfoData = (): ProgressInfoData => {
    let confirmedQuestionsCount = 0;
    let skillsCount = 0;
    let skillsWithConfirmedQuestionCount = 0;
    const skillsSummary: SkillProgressInfoData[] = [];

    competencyMap?.competences?.forEach((competence) => {
      competence.knowledgeBlockCompetences?.forEach(({ knowledgeBlock }) => {
        knowledgeBlock.skills?.forEach((skill) => {
          const { questions } = skill;
          const confirmedCount = questions?.filter((q) => q.confirmation).length || 0;
          confirmedQuestionsCount += confirmedCount;
          skillsCount += 1;

          if (confirmedCount) {
            skillsWithConfirmedQuestionCount += 1;
          }

          skillsSummary.push({
            id: skill.id,
            title: skill.title,
            confirmedQuestionsCount: confirmedCount,
          });
        });
      });
    });

    return [
      {
        confirmedQuestionsCount,
        progressPercent: skillsCount ? Math.round((skillsWithConfirmedQuestionCount / skillsCount) * 100) : 0,
      },
      skillsSummary,
    ];
  };

  const [{ confirmedQuestionsCount, progressPercent }, skills] = getProgressInfoData();

  const progressStatisticsInfo = [
    {
      label: "Всего вопросов в карте",
      value: confirmedQuestionsCount,
    },
    {
      label: "Среднее время тестирования",
      value: secondsToMinutes(competencyMap?.questionsTime),
    },
  ];

  return (
    <Card variant='dense'>
      <Stack spacing={2.5}>
        <Stack spacing={1}>
          <Stack
            direction='row'
            justifyContent='space-between'>
            <Typography sx={{ fontWeight: 700 }}>Прогресс заполнения</Typography>
            <Typography sx={{ fontWeight: 700 }}>{progressPercent}%</Typography>
          </Stack>
          <LinearProgress
            variant='determinate'
            color={progressPercent === 100 ? "primary" : "warning"}
            value={progressPercent}
            sx={{ height: 10, borderRadius: 2 }}
          />
        </Stack>
        <Stack
          spacing={1}
          sx={{ maxHeight: 400, overflow: "auto" }}>
          {skills.length ? (
            skills.map((skill) => (
              <Stack
                key={skill.id}
                direction='row'
                justifyContent='space-between'
                sx={{
                  width: "100%",
                  padding: 1,
                  marginTop: 1,
                  border: "1px solid",
                  borderColor: "border",
                  borderRadius: 2,
                }}>
                <Typography
                  title={skill.title}
                  sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {skill.title}
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>{skill.confirmedQuestionsCount}</Typography>
              </Stack>
            ))
          ) : (
            <Typography>Пока нет навыков</Typography>
          )}
        </Stack>
        <Stack
          direction='row'
          spacing={1}>
          {progressStatisticsInfo.map((item, index) => (
            <Stack
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              spacing={0.5}
              sx={{ width: "50%", padding: 1, backgroundColor: "bgSwitch", borderRadius: 2 }}>
              <Typography
                variant='body14rg'
                sx={{ color: "base.300" }}>
                {item.label}
              </Typography>
              <Typography sx={{ fontWeight: 700 }}>{item.value}</Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Card>
  );
};

export default ProgressInfo;
